import { useQuery } from "@tanstack/react-query";

import { describeError } from "@/api/client";
import { useCanEdit } from "@/api/session";
import { workspaceQuery } from "@/api/workspace";
import { Button, InlineAlert, PageHeader, Skeleton, Tabs } from "@/components";
import { useDelayedVisibility, useSearchParamsInPlace } from "@/lib/hooks";
import { AppearanceSection } from "./sections/AppearanceSection";
import { BenchSection } from "./sections/BenchSection";
import { CalendarSection } from "./sections/CalendarSection";
import { LabelsSection } from "./sections/LabelsSection";
import { SchedulesSection } from "./sections/SchedulesSection";
import { WorkspaceSection } from "./sections/WorkspaceSection";
import styles from "./settings.module.css";

const TABS = [
  { value: "workspace", label: "Workspace" },
  { value: "appearance", label: "Appearance" },
  { value: "bench", label: "Bench" },
  { value: "labels", label: "Labels" },
  { value: "calendar", label: "Calendar" },
  { value: "schedules", label: "Schedules" },
] as const;

type TabValue = (typeof TABS)[number]["value"];

function isTab(value: string | null): value is TabValue {
  return TABS.some((tab) => tab.value === value);
}

export function SettingsPage() {
  const [searchParams, setSearchParams] = useSearchParamsInPlace();
  const requestedTab = searchParams.get("tab");
  const tab: TabValue = isTab(requestedTab) ? requestedTab : "workspace";
  const workspace = useQuery(workspaceQuery);
  const canEdit = useCanEdit();
  const showSkeleton = useDelayedVisibility(workspace.isPending);

  return (
    <div className={styles.page}>
      <PageHeader
        title="Settings"
        description="Ship-to address and markets, appearance, bench tolerances, label calibration, the reminders calendar and scheduled jobs."
      />
      {!canEdit ? (
        <InlineAlert tone="info" title="You have view-only access">
          Ask the workspace owner to change these settings. Theme and keyboard choices still apply on this device.
        </InlineAlert>
      ) : null}
      {workspace.isPending ? (
        showSkeleton ? (
          <div className={styles.loading} aria-busy="true">
            <Skeleton shape="control" />
            <Skeleton shape="control" />
            <Skeleton shape="control" />
          </div>
        ) : null
      ) : workspace.isError ? (
        <InlineAlert
          tone="danger"
          title="Couldn’t load the settings"
          action={<Button onClick={() => void workspace.refetch()}>Retry</Button>}
        >
          {describeError(workspace.error)}
        </InlineAlert>
      ) : (
        <Tabs.Root value={tab} onValueChange={(next) => setSearchParams({ tab: next }, { replace: true })}>
          <Tabs.List label="Settings sections">
            {TABS.map((item) => (
              <Tabs.Tab key={item.value} value={item.value}>
                {item.label}
              </Tabs.Tab>
            ))}
          </Tabs.List>
          <Tabs.Panel value="workspace">
            <WorkspaceSection workspace={workspace.data} canEdit={canEdit} />
          </Tabs.Panel>
          <Tabs.Panel value="appearance">
            <AppearanceSection />
          </Tabs.Panel>
          <Tabs.Panel value="bench">
            <BenchSection workspace={workspace.data} canEdit={canEdit} />
          </Tabs.Panel>
          <Tabs.Panel value="labels">
            <LabelsSection workspace={workspace.data} canEdit={canEdit} />
          </Tabs.Panel>
          <Tabs.Panel value="calendar">
            <CalendarSection canEdit={canEdit} />
          </Tabs.Panel>
          <Tabs.Panel value="schedules">
            <SchedulesSection workspace={workspace.data} canEdit={canEdit} />
          </Tabs.Panel>
        </Tabs.Root>
      )}
    </div>
  );
}
